import * as z from 'zod'

// Valid Omni content path patterns:
// - /chat
// - /dashboards/<id> (e.g. /dashboards/12cd56gh)
// - /apps/<id> (e.g. /apps/12cd56gh)
// - /w/<id>/duplicate (e.g. /w/0238af56/duplicate)
const VALID_CONTENT_PATH_PATTERN =
  /^\/(?:chat|dashboards\/[a-f0-9]+|apps\/[a-f0-9]+|w\/[a-f0-9]+\/duplicate)$/i

function normalizeContentPath(url: string): string {
  url = url.trim()
  if (!url.startsWith('/')) {
    url = '/' + url
  }
  return url
}

export const dashboardSchema = z.object({
  title: z.string().min(1),
  dashboard_url: z
    .string()
    .transform(normalizeContentPath)
    .refine((url) => VALID_CONTENT_PATH_PATTERN.test(url), {
      message: 'Expected `/chat`, `/dashboards/<id>`, `/apps/<id>`, or `/w/<id>/duplicate`',
    }),
  allowed_member_ids: z.array(z.string()).nullable().optional(),
})

export type DashboardFormSchema = z.output<typeof dashboardSchema>

export const analysisSchema = z.object({
  title: z.string().min(1),
  icon: z.string().optional(),
  dashboards: z.array(dashboardSchema).min(1),
  allowed_member_ids: z.array(z.string()).nullable().optional(),
})

export type AnalysisFormSchema = z.output<typeof analysisSchema>

export const organizationAnalysisFormSchema = z.object({
  analyses: z.array(analysisSchema).min(1),
})

export type OrganizationAnalysisFormSchema = z.output<typeof organizationAnalysisFormSchema>

export const organizationFormSchema = z.object({
  name: z.string().min(1),
  slug: z.string().regex(/^[a-z0-9-]+$/),
  logo_file: z.file().mime(['image/png', 'image/jpeg']).optional(),
  // '' (field cleared) is kept so the backend can reset the value; undefined means untouched
  client_name: z
    .string()
    .optional()
    .transform((v) => v?.trim()),
})

export type OrganizationFormSchema = z.output<typeof organizationFormSchema>

export const inviteUsersFormSchema = z.object({
  email: z.email().min(1),
  name: z.string().min(1),
})

export type InviteUsersFormSchema = z.output<typeof inviteUsersFormSchema>

// Notion page ID: 32-character hex string (with or without dashes)
// e.g. "1a682ab5188980899564c2a9152bc5e3" or "1a682ab5-1889-8089-9564-c2a9152bc5e3"
const NOTION_PAGE_ID_PATTERN = /^[a-f0-9]{32}$/i

function normalizeNotionPageId(id: string): string {
  return id.trim().replace(/-/g, '')
}

export const homepageFormSchema = z.object({
  notion_page_id: z
    .string()
    .min(1, 'Page ID is required')
    .transform(normalizeNotionPageId)
    .refine((id) => NOTION_PAGE_ID_PATTERN.test(id), {
      message: 'Invalid Notion page ID. Expected 32-character hex string.',
    }),
})

export type HomepageFormSchema = z.output<typeof homepageFormSchema>

// Brand schemas
const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/

export const brandCreateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(100)
    .regex(SLUG_PATTERN, 'Slug must be lowercase alphanumeric with hyphens'),
  website: z.string().max(255).optional(),
})

export type BrandCreateSchema = z.output<typeof brandCreateSchema>

export const brandUpdateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  website: z.string().max(255).optional(),
})

export type BrandUpdateSchema = z.output<typeof brandUpdateSchema>

// Platform Advertiser schemas
export const platformAdvertiserCreateSchema = z.object({
  platform_id: z.string().uuid('Platform is required'),
  platform_advertiser_identifier: z.string().min(1, 'Advertiser ID is required').max(255),
  country_code: z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/)
    .nullable()
    .optional(),
})

export type PlatformAdvertiserCreateSchema = z.output<typeof platformAdvertiserCreateSchema>

// Top-level advertiser creation (includes brand selection)
export const advertiserCreateSchema = platformAdvertiserCreateSchema.extend({
  brand_id: z.string().uuid('Brand is required'),
  sector_ids: z.array(z.string().uuid()).optional(),
})

export type AdvertiserCreateSchema = z.output<typeof advertiserCreateSchema>

export const platformAdvertiserUpdateSchema = z.object({
  platform_advertiser_identifier: z.string().min(1, 'Advertiser ID is required').max(255),
  country_code: z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/)
    .nullable()
    .optional(),
})

export type PlatformAdvertiserUpdateSchema = z.output<typeof platformAdvertiserUpdateSchema>

// Sector schemas
export const sectorCreateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(100)
    .regex(SLUG_PATTERN, 'Slug must be lowercase alphanumeric with hyphens'),
  emoji: z.string().max(10).optional(),
})

export type SectorCreateSchema = z.output<typeof sectorCreateSchema>

export const sectorUpdateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  emoji: z.string().max(10).optional(),
})

export type SectorUpdateSchema = z.output<typeof sectorUpdateSchema>
